import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useLocale } from "../i18n/LocaleContext";
import VideoCard from "./cinema/VideoCard";
import VideoModal from "./cinema/VideoModal";

/**
 * CinemaCarousel — premium dark YouTube reel.
 *
 * Now publicly accessible (no longer member-gated). The backend
 * `/api/videos` endpoint takes care of filtering: guests receive only
 * `is_member_only=false` videos, authenticated members receive everything.
 * Pass `filter="public"` or `filter="exclusive"` to render only one of the
 * two rails; omit it to render the full list (e.g. used on the home).
 */
export default function CinemaCarousel({ filter = null, title = null, overline = null }) {
  const { t } = useLocale();
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null);
  const railRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/videos")
      .then((r) => {
        if (cancelled) return;
        setVideos(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = filter
    ? videos.filter((v) =>
        filter === "exclusive" ? v.is_member_only : !v.is_member_only
      )
    : videos;

  if (!loaded || filtered.length === 0) return null;
  if (!loaded || filtered.length === 0) return null;

  const scroll = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const dx = Math.round(el.clientWidth * 0.85) * (dir === "next" ? 1 : -1);
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  return (
    <section
      className="relative py-24 sm:py-28 border-t overflow-hidden"
      style={{ borderColor: "var(--border-subtle)" }}
      data-testid="cinema-section"
    >
      {/* Faint ambient red — luxury mood */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 40% at 50% 100%, rgba(255,40,0,0.10), transparent 70%)",
          mixBlendMode: "screen",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 mb-10 sm:mb-14">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex items-end justify-between gap-6"
        >
          <div>
            <span className="overline mb-3 block">{overline || t("cinema.overline")}</span>
            <h2
              className="text-3xl sm:text-5xl font-light text-balance"
              style={{ letterSpacing: "-0.01em" }}
            >
              {title || t("cinema.headline")}
            </h2>
            {!filter && (
              <p
                className="mt-4 max-w-xl text-sm sm:text-base"
                style={{ color: "var(--text-secondary)" }}
              >
                {t("cinema.body")}
              </p>
            )}
          </div>

          {filtered.length > 1 && (
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scroll("prev")}
                aria-label="Previous"
                className="p-3 transition-colors hover:text-white"
                style={{
                  color: "var(--text-secondary)",
                  border: "1px solid var(--border-subtle)",
                }}
                data-testid="cinema-prev-btn"
              >
                <ChevronLeft size={16} strokeWidth={1.4} />
              </button>
              <button
                type="button"
                onClick={() => scroll("next")}
                aria-label="Next"
                className="p-3 transition-colors hover:text-white"
                style={{
                  color: "var(--text-secondary)",
                  border: "1px solid var(--border-subtle)",
                }}
                data-testid="cinema-next-btn"
              >
                <ChevronRight size={16} strokeWidth={1.4} />
              </button>
            </div>
          )}
        </motion.div>
      </div>

      <div
        ref={railRef}
        className="relative flex gap-5 sm:gap-7 overflow-x-auto snap-x snap-mandatory px-6 sm:px-12 pb-4"
        style={{
          scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch",
        }}
        data-testid="cinema-rail"
      >
        <style>{`[data-testid="cinema-rail"]::-webkit-scrollbar{display:none;}`}</style>
        {filtered.map((v, i) => (
          <VideoCard
            key={v.id}
            video={v}
            onOpen={setActive}
            index={i}
            featured={i === 0}
          />
        ))}
        <span
          aria-hidden
          className="shrink-0"
          style={{ width: "1px", height: "1px" }}
        />
      </div>

      <AnimatePresence>
        {active && <VideoModal video={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
